import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  Link as MuiLink,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { CheckCircleOutline, HourglassEmpty } from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ttsApi } from '@/api/tts'
import { useBalanceStore } from '@/stores/balanceStore'
import { formatMoney } from '@/components/billing/formatMoney'
import { getErrorMessage, parseStorageLimitError, type StorageLimitErrorPayload } from '@/utils/error'
import { bytesToMb } from '@/utils/storage'
import { ROUTES } from '@/utils/constants'
import type { TtsBatch, TtsEstimate } from '@/types/tts'

type DialogPhase = 'estimating' | 'confirm' | 'submitting' | 'queued' | 'done'

interface GenerateAudioDialogProps {
  open: boolean
  wordIds: number[]
  onClose: () => void
  /** Вызывается после постановки в очередь или мгновенного завершения (обновить список слов) */
  onFinished: () => void
}

export function GenerateAudioDialog({ open, wordIds, onClose, onFinished }: GenerateAudioDialogProps) {
  const { t } = useTranslation()
  const applyBalance = useBalanceStore((s) => s.applyBalance)

  const [phase, setPhase] = useState<DialogPhase>('estimating')
  const [estimate, setEstimate] = useState<TtsEstimate | null>(null)
  const [batch, setBatch] = useState<TtsBatch | null>(null)
  const [force, setForce] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** 422 storage_limit_exceeded от сервера: занято/лимит из payload ошибки (могут отсутствовать) */
  const [storageError, setStorageError] = useState<StorageLimitErrorPayload | null>(null)

  const loadEstimate = useCallback(
    async (withForce: boolean) => {
      setPhase('estimating')
      setError(null)
      setStorageError(null)
      try {
        const result = await ttsApi.estimate(wordIds, withForce)
        setEstimate(result)
        setPhase('confirm')
      } catch (err) {
        setError(getErrorMessage(err) || t('common.error'))
        setPhase('confirm')
      }
    },
    [wordIds, t],
  )

  useEffect(() => {
    if (open && wordIds.length > 0) {
      setBatch(null)
      setForce(false)
      void loadEstimate(false)
    }
  }, [open, wordIds, loadEstimate])

  const handleForceChange = (checked: boolean) => {
    setForce(checked)
    void loadEstimate(checked)
  }

  const handleGenerate = async () => {
    setError(null)
    setStorageError(null)
    setPhase('submitting')
    try {
      const { batch: created, balance } = await ttsApi.generate(wordIds, force)
      applyBalance(balance)
      setBatch(created)
      // Если всё пропущено/из кеша — батч завершается мгновенно
      setPhase(created.status === 'finished' ? 'done' : 'queued')
      onFinished()
    } catch (err) {
      const storageLimit = parseStorageLimitError(err)
      if (storageLimit !== null) {
        setStorageError(storageLimit)
      } else {
        setError(getErrorMessage(err) || t('common.error'))
      }
      setPhase('confirm')
    }
  }

  const hasWordsWithAudio = estimate?.items.some((item) => item.has_audio) ?? false
  const nothingToGenerate = estimate !== null && !estimate.items.some((item) => item.will_generate)
  const insufficient = estimate !== null && !estimate.sufficient
  const storageInsufficient = (estimate?.storage ? !estimate.storage.sufficient : false) || storageError !== null
  // Симметричный фолбэк: если в 422 не пришли цифры — обе берём из сметы
  const storageUsedBytes = storageError?.usedBytes ?? estimate?.storage?.used_bytes ?? null
  const storageLimitBytes = storageError?.limitBytes ?? estimate?.storage?.limit_bytes ?? null

  // Цифры показываем, когда они известны; иначе — сообщение сервера
  let storageMessage: string
  if (storageUsedBytes === null) {
    storageMessage = storageError?.message ?? t('common.error')
  } else if (storageLimitBytes !== null) {
    storageMessage = t('tts.storageLimitExceeded', 'Недостаточно места для аудио: занято {{used}} из {{limit}} МБ.', {
      used: bytesToMb(storageUsedBytes),
      limit: bytesToMb(storageLimitBytes),
    })
  } else {
    storageMessage = t('tts.storageLimitExceededNoLimit', 'Недостаточно места для аудио: занято {{used}} МБ.', {
      used: bytesToMb(storageUsedBytes),
    })
  }

  return (
    <Dialog open={open} onClose={phase === 'submitting' ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('tts.dialogTitle', 'Озвучивание слов')}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {(phase === 'estimating' || phase === 'submitting') && (
          <Box sx={{ py: 1 }}>
            {[1, 2, 3].map((row) => (
              <Skeleton key={row} variant="rounded" height={36} sx={{ mb: 1 }} />
            ))}
            <Skeleton variant="rounded" height={24} width="50%" />
          </Box>
        )}

        {phase === 'confirm' && estimate && (
          <>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('tts.word', 'Слово')}</TableCell>
                  <TableCell align="right">{t('tts.chars', 'Символов')}</TableCell>
                  <TableCell align="right">{t('tts.price', 'Цена')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {estimate.items.map((item) => (
                  <TableRow key={item.word_id}>
                    <TableCell>
                      {item.text}
                      {item.has_audio && (
                        <Chip
                          size="small"
                          sx={{ ml: 1 }}
                          label={t('tts.hasAudio', 'есть аудио')}
                        />
                      )}
                      {item.cached && item.will_generate && (
                        <Chip
                          size="small"
                          color="success"
                          sx={{ ml: 1 }}
                          label={t('tts.cachedDiscount', 'повтор −{{percent}}%', {
                            percent: estimate.cache_discount_percent,
                          })}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">{item.chars}</TableCell>
                    <TableCell align="right">
                      {item.will_generate
                        ? formatMoney(item.price, estimate.currency)
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {hasWordsWithAudio && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={force}
                    onChange={(e) => handleForceChange(e.target.checked)}
                    size="small"
                  />
                }
                label={t('tts.forceRegenerate', 'Перегенерировать слова с готовым аудио')}
              />
            )}

            <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
              <Typography>
                {t('tts.total', 'Итого')}: <b>{formatMoney(estimate.total_price, estimate.currency)}</b>
              </Typography>
              <Typography color={insufficient ? 'error' : 'text.secondary'}>
                {t('tts.balance', 'Баланс')}: {formatMoney(estimate.balance, estimate.currency)}
              </Typography>
            </Stack>

            {insufficient && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('tts.insufficientBalance', 'Недостаточно средств на балансе.')}{' '}
                <MuiLink component={RouterLink} to={ROUTES.PROFILE_BALANCE}>
                  {t('tts.topupLink', 'Пополнить баланс')}
                </MuiLink>
              </Alert>
            )}

            {storageInsufficient && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {storageMessage}{' '}
                <MuiLink component={RouterLink} to={ROUTES.PROFILE_SUBSCRIPTION}>
                  {t('tts.changePlanLink', 'Сменить тариф')}
                </MuiLink>
              </Alert>
            )}

            {nothingToGenerate && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('tts.nothingToGenerate', 'У всех выбранных слов уже есть аудио.')}
              </Alert>
            )}
          </>
        )}

        {phase === 'queued' && batch && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <HourglassEmpty color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('tts.queuedTitle', 'Задача поставлена в очередь')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('tts.queuedBody', {
                count: batch.total_count - batch.skipped_count,
                defaultValue:
                  'Озвучивается слов: {{count}}. Как только всё будет готово — вы получите уведомление.',
              })}
            </Typography>
            <MuiLink component={RouterLink} to={ROUTES.TTS_TASKS} onClick={onClose}>
              {t('tts.tasksLink', 'Смотреть задачи озвучки')}
            </MuiLink>
          </Box>
        )}

        {phase === 'done' && batch && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleOutline color="success" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('tts.finished', 'Готово')}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              <Chip size="small" color="success" label={`${t('tts.done', 'озвучено')}: ${batch.done_count}`} />
              {batch.skipped_count > 0 && (
                <Chip size="small" label={`${t('tts.skipped', 'пропущено')}: ${batch.skipped_count}`} />
              )}
              {batch.failed_count > 0 && (
                <Chip size="small" color="error" label={`${t('tts.failed', 'ошибок')}: ${batch.failed_count}`} />
              )}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {phase === 'confirm' && (
          <>
            <Button onClick={onClose}>{t('common.cancel', 'Отмена')}</Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={!estimate || insufficient || storageInsufficient || nothingToGenerate}
            >
              {t('tts.generate', 'Озвучить')}
              {estimate && parseFloat(estimate.total_price) > 0
                ? ` (${formatMoney(estimate.total_price, estimate.currency)})`
                : ''}
            </Button>
          </>
        )}
        {(phase === 'queued' || phase === 'done') && (
          <Button variant="contained" onClick={onClose}>
            {t('common.close', 'Закрыть')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
